/**
 * AsTooltipWrapper Component
 * 
 * A wrapper component that creates a tooltip attached to its child elements.
 * It supports multiple display modes: show-on-hover, toggle-on-click, or manual control.
 * Uses @floating-ui/dom for robust positioning with collision detection and boundary awareness.
 * 
 * Attributes:
 * - textKey: Localization key for the tooltip text
 * - default-text: Default text to display if no translation is found
 * - show: When present, displays the tooltip
 * - placement: Position of the tooltip (top, bottom, left, right, etc.)
 * - show-on-hover: When present, shows tooltip on hover
 * - toggle-on-click: When present, toggles tooltip visibility on click
 * - no-arrow: When present, hides the tooltip arrow (passed to as-tooltip)
 * - auto-flip: When present, allows the tooltip to flip position when near viewport edges
 * - offset: Distance in pixels from the trigger element (default: 8)
 */
import { computePosition, flip, shift, offset, autoUpdate } from '@floating-ui/dom';
import './as-tooltip.ts';
import styleText from './as-tooltip-wrapper.css?raw';

export class AsTooltipWrapper extends HTMLElement {
  private cleanup?: () => void;
  private intersectionObserver?: IntersectionObserver;
  private shouldBeVisible: boolean = false;
  private flashTimeout?: number;
  private portaledTooltip?: HTMLElement;
  private _pinned = false;
  private static readonly sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styleText);
    return sheet;
  })();

  static get observedAttributes() {
    return ['dict-key', 'default-text', 'show', 'placement', 'show-on-hover', 'toggle-on-click', 'no-arrow', 'offset', 'auto-flip', 'flash', 'flash-duration', 'flash-delay', 'active-tooltip'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot!.adoptedStyleSheets = [
      ...this.shadowRoot!.adoptedStyleSheets,
      AsTooltipWrapper.sheet
    ];
  }

  /**
   * Map our placement terms to Floating UI placements
   */
  private mapPlacement(placement: string) {
    const placementMap: { [key: string]: string } = {
      'top': 'top',
      'bottom': 'bottom',
      'left': 'left',
      'right': 'right',
      'top-left': 'top-end',
      'top-right': 'top-start',
      'bottom-left': 'bottom-end',
      'bottom-right': 'bottom-start'
    };
    return placementMap[placement] || 'bottom';
  }

  /**
   * Get offset value
   */
  private getOffset(): number {
    return parseInt(this.getAttribute('offset') ?? '8');
  }

  /**
   * Flash the tooltip - show it briefly then hide it
   */
  private flashTooltip(): void {
    // Clear any existing flash timeout
    if (this.flashTimeout) {
      clearTimeout(this.flashTimeout);
      this.flashTimeout = undefined;
    }

    // Get flash delay (default 0ms) and duration (default 2000ms)
    const delay = parseInt(this.getAttribute('flash-delay') ?? '0');
    const duration = parseInt(this.getAttribute('flash-duration') ?? '2000');

    // Wait for delay, then show the tooltip
    this.flashTimeout = window.setTimeout(() => {
      // Create and show the tooltip
      this.createPortaledTooltip();
      this.shouldBeVisible = true;
      this.portaledTooltip?.setAttribute('show', '');
      this.updateTooltipPosition();

      // Hide after duration and remove flash attribute
      this.flashTimeout = window.setTimeout(() => {
        this.shouldBeVisible = false;
        this.portaledTooltip?.removeAttribute('show');

        // Clean up autoUpdate when hiding
        if (this.cleanup) {
          this.cleanup();
          this.cleanup = undefined;
        }

        // Remove portaled tooltip
        this.removePortaledTooltip();

        // Remove the flash attribute to prevent re-triggering
        this.removeAttribute('flash');
        this.flashTimeout = undefined;
      }, duration);
    }, delay);
  }

  /**
   * Set up intersection observer to auto-hide tooltip when scrolled out of view
   */
  private setupIntersectionObserver() {
    const wrapperElement = this.shadowRoot?.querySelector('.tooltip-wrapper') as HTMLElement;
    if (!wrapperElement) return;

    // Clean up existing observer
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }

    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const tooltip = this.portaledTooltip;
        if (!tooltip) return;

        if (!entry.isIntersecting) {
          // Element is scrolled out of view - hide tooltip if visible
          if (tooltip.hasAttribute('show')) {
            tooltip.removeAttribute('show');
            // Clean up autoUpdate when hiding
            if (this.cleanup) {
              this.cleanup();
              this.cleanup = undefined;
            }
          }
        } else {
          // Element is back in view - restore tooltip if it should be visible
          if (this.shouldBeVisible && !tooltip.hasAttribute('show')) {
            tooltip.setAttribute('show', '');
            this.updateTooltipPosition();
          }
        }
      });
    }, {
      // Use a small margin to hide tooltips slightly before they go completely off-screen
      rootMargin: '-10px'
    });

    this.intersectionObserver.observe(wrapperElement);
  }

  /**
   * Update tooltip position using Floating UI
   */
  private updateTooltipPosition() {
    const tooltip = this.portaledTooltip;
    const wrapperElement = this.shadowRoot?.querySelector('.tooltip-wrapper') as HTMLElement;

    if (!tooltip || !wrapperElement) return;

    const placement = this.getAttribute('placement') || 'bottom';
    const floatingPlacement = this.mapPlacement(placement);
    const offsetValue = this.getOffset();

    // Build middleware array conditionally
    const middleware = [offset(offsetValue)];

    // Only add flip if auto-flip attribute is present
    if (this.hasAttribute('auto-flip')) {
      middleware.push(flip());
    }

    middleware.push(shift({ padding: 5 }));

    // Clean up any existing autoUpdate
    if (this.cleanup) {
      this.cleanup();
    }

    // Set up intersection observer for auto-hiding
    this.setupIntersectionObserver();

    // Set up autoUpdate to handle scroll/resize events
    this.cleanup = autoUpdate(wrapperElement, tooltip, async () => {
      try {
        const { x, y } = await computePosition(wrapperElement, tooltip, {
          placement: floatingPlacement as any,
          strategy: 'fixed',
          middleware: middleware,
        });

        // Apply the computed position
        Object.assign(tooltip.style, {
          left: `${x}px`,
          top: `${y}px`,
          transform: 'none',
          position: 'fixed',
          zIndex: '80',
        });
      } catch (error) {
        console.warn('Failed to position tooltip:', error);
      }
    });
  }

  /**
   * Set up the component template with a single source of truth approach
   */
  private createTemplate(): DocumentFragment {
    const template = document.createElement('template');

    template.innerHTML = `
      <div class="tooltip-wrapper"
           ${this.hasAttribute('show-on-hover') ? 'data-hover="true"' : ''}
           ${this.hasAttribute('toggle-on-click') ? 'data-click="true"' : ''}>
        <slot></slot>
      </div>
    `;

    return template.content.cloneNode(true) as DocumentFragment;
  }

  /**
   * Create the portaled tooltip element in document.body.
   * If the wrapper has as-tooltip[name] children, the one matching nameOverride
   * (or the active-tooltip attribute, defaulting to "default") is cloned as content.
   * Elements with [data-dismiss] inside named tooltips close the pinned tooltip on click.
   */
  private createPortaledTooltip(nameOverride?: string): void {
    if (this.portaledTooltip) return;

    const tooltip = document.createElement('as-tooltip');

    const placement = this.getAttribute('placement');
    if (placement) tooltip.setAttribute('placement', placement);
    if (this.hasAttribute('no-arrow')) tooltip.setAttribute('no-arrow', '');

    const name = nameOverride ?? this.getAttribute('active-tooltip') ?? 'default';
    const namedChild = this.querySelector(`as-tooltip[name="${name}"]`)
      ?? (name === 'default' ? this.querySelector('as-tooltip') : null);

    if (namedChild) {
      const hasInteractive = !!namedChild.querySelector('button, input, a, [tabindex], as-icon-button, as-button');
      Object.assign(tooltip.style, {
        position: 'fixed', zIndex: '80',
        pointerEvents: hasInteractive ? 'auto' : 'none',
        maxWidth: hasInteractive ? 'none' : '',
      });
      tooltip.innerHTML = namedChild.innerHTML;
      if (hasInteractive) {
        tooltip.querySelectorAll('[data-dismiss]').forEach(el => {
          el.addEventListener('click', () => this._closePin());
        });
        tooltip.querySelectorAll('[data-confirm]').forEach(el => {
          el.addEventListener('click', () => {
            this._closePin();
            this.dispatchEvent(new CustomEvent('as-tooltip-confirm', { bubbles: true, composed: true }));
          });
        });
      }
    } else {
      Object.assign(tooltip.style, { position: 'fixed', zIndex: '80', pointerEvents: 'none' });
      const asText = document.createElement('as-text');
      const dictKey = this.getAttribute('dict-key');
      if (dictKey) asText.setAttribute('dict-key', dictKey);
      asText.textContent = this.getAttribute('default-text') ?? '';
      tooltip.appendChild(asText);
    }

    document.body.appendChild(tooltip);
    this.portaledTooltip = tooltip;
  }

  /** Pin a named tooltip open (stays visible until dismissed, ESC, or outside click). */
  showNamedTooltip(name: string): void {
    this._closePin();
    this._pinned = true;
    this.shouldBeVisible = true;
    this.createPortaledTooltip(name);
    this.portaledTooltip?.setAttribute('show', '');
    this.updateTooltipPosition();
    document.addEventListener('keydown', this._handlePinKeydown);
    // Defer outside-click listener so the triggering click doesn't immediately close it
    setTimeout(() => {
      if (this._pinned) document.addEventListener('click', this._handlePinOutsideClick, true);
    }, 0);
  }

  private _handlePinKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') this._closePin();
  };

  private _handlePinOutsideClick = (e: MouseEvent): void => {
    const t = e.target as Node;
    if (this.portaledTooltip?.contains(t) || this.contains(t)) return;
    this._closePin();
  };

  private _closePin(): void {
    document.removeEventListener('keydown', this._handlePinKeydown);
    document.removeEventListener('click', this._handlePinOutsideClick, true);
    this._pinned = false;
    this.shouldBeVisible = false;
    this.portaledTooltip?.removeAttribute('show');
    if (this.cleanup) { this.cleanup(); this.cleanup = undefined; }
    this.removePortaledTooltip();
  }

  /**
   * Remove the portaled tooltip from document.body
   */
  private removePortaledTooltip(): void {
    if (this.portaledTooltip) {
      this.portaledTooltip.remove();
      this.portaledTooltip = undefined;
    }
  }

  /**
   * Handle attribute changes with a reactive approach
   */
  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    // Skip if component isn't connected yet
    if (!this.shadowRoot || !this.isConnected) return;

    // Skip if nothing changed
    if (oldValue === newValue) return;

    const tooltip = this.portaledTooltip;

    // Handle specific attribute changes
    switch (name) {
      case 'show':
        if (newValue === null) {
          this.shouldBeVisible = false;
          if (tooltip) {
            tooltip.removeAttribute('show');
          }
          // Clean up autoUpdate when hiding
          if (this.cleanup) {
            this.cleanup();
            this.cleanup = undefined;
          }
          // Remove portaled tooltip when hiding
          this.removePortaledTooltip();
        } else {
          this.shouldBeVisible = true;
          this.createPortaledTooltip();
          this.portaledTooltip?.setAttribute('show', '');
          // Update position when showing
          this.updateTooltipPosition();
        }
        break;

      case 'placement':
        // Pass placement to tooltip component for arrow positioning
        if (tooltip) {
          if (newValue === null) {
            tooltip.removeAttribute('placement');
          } else {
            tooltip.setAttribute('placement', newValue);
          }
          // Update tooltip position if currently visible
          if (tooltip.hasAttribute('show')) {
            this.updateTooltipPosition();
          }
        }
        break;

      case 'offset':
        // Update tooltip position if currently visible
        if (tooltip?.hasAttribute('show')) {
          this.updateTooltipPosition();
        }
        break;

      case 'auto-flip':
        // Update tooltip position if currently visible since flip behavior changed
        if (tooltip?.hasAttribute('show')) {
          this.updateTooltipPosition();
        }
        break;

      case 'no-arrow':
        if (tooltip) {
          if (newValue === null) {
            tooltip.removeAttribute(name);
          } else {
            tooltip.setAttribute(name, newValue ?? '');
          }
        }
        break;

      case 'dict-key':
      case 'default-text':
        if (tooltip) {
          const textElement = tooltip.querySelector('as-text');
          if (textElement) {
            if (name === 'dict-key') {
              textElement.setAttribute('dict-key', newValue ?? '');
            } else {
              textElement.textContent = newValue ?? '';
            }
          }
        }
        break;

      case 'show-on-hover':
      case 'toggle-on-click':
        // These require re-rendering to update the event handlers
        this.render();
        break;

      case 'flash':
        if (newValue !== null) {
          // Flash attribute added, trigger the flash
          this.flashTooltip();
        } else {
          // Flash attribute removed, cancel any ongoing flash
          if (this.flashTimeout) {
            clearTimeout(this.flashTimeout);
            this.flashTimeout = undefined;
          }
        }
        break;

      case 'flash-duration':
        break;

      case 'active-tooltip':
        if (this.shouldBeVisible && !this._pinned && this.portaledTooltip) {
          this.removePortaledTooltip();
          this.createPortaledTooltip();
          this.portaledTooltip?.setAttribute('show', '');
          this.updateTooltipPosition();
        }
        break;
    }
  }

  /**
   * Set up event delegation
   */
  private setupEvents() {
    const wrapper = this.shadowRoot?.querySelector('.tooltip-wrapper');
    if (!wrapper) return;

    // Use event delegation to handle all events in one place
    wrapper.addEventListener('pointerenter', this.handleEvent);
    wrapper.addEventListener('pointerleave', this.handleEvent);
    wrapper.addEventListener('click', this.handleEvent);

    // Listen for nested tooltip-wrapper hovers to hide our tooltip
    this.addEventListener('as-tooltip-hover', this.handleNestedHover);
  }
  
  /**
   * Hide tooltip when a nested tooltip-wrapper is hovered
   */
  private handleNestedHover = (event: Event) => {
    // Don't react to our own events
    if (event.target === this) return;

    const tooltip = this.portaledTooltip;
    if (tooltip?.hasAttribute('show')) {
      tooltip.removeAttribute('show');
      this.shouldBeVisible = false;
      if (this.cleanup) {
        this.cleanup();
        this.cleanup = undefined;
      }
      this.removePortaledTooltip();
    }
  };

  /**
   * Unified event handler using dataset properties for behavior control
   */
  private handleEvent = (event: Event) => {
    const wrapper = event.currentTarget as HTMLElement;

    // Handle hover events
    if (wrapper.dataset.hover === 'true') {
      if (event.type === 'pointerenter') {
        // Notify ancestor tooltip-wrappers to hide their tooltips
        this.dispatchEvent(new CustomEvent('as-tooltip-hover', {
          bubbles: true,
          composed: true
        }));

        this.shouldBeVisible = true;
        this.createPortaledTooltip();
        this.portaledTooltip?.setAttribute('show', '');
        // Update position when showing
        this.updateTooltipPosition();
      } else if (event.type === 'pointerleave') {
        if (!this._pinned) {
          this.shouldBeVisible = false;
          this.portaledTooltip?.removeAttribute('show');
          if (this.cleanup) { this.cleanup(); this.cleanup = undefined; }
          this.removePortaledTooltip();
        }
      }
    }

    // Handle click events
    if (wrapper.dataset.click === 'true' && event.type === 'click') {
      if (this.portaledTooltip?.hasAttribute('show')) {
        this.shouldBeVisible = false;
        this.portaledTooltip.removeAttribute('show');
        // Clean up autoUpdate when hiding
        if (this.cleanup) {
          this.cleanup();
          this.cleanup = undefined;
        }
        this.removePortaledTooltip();
      } else {
        this.shouldBeVisible = true;
        this.createPortaledTooltip();
        this.portaledTooltip?.setAttribute('show', '');
        // Update position when showing
        this.updateTooltipPosition();
      }
    }
  }

  /**
   * Main render method
   */
  private render() {
    if (!this.shadowRoot) return;
    
    // Clear existing content
    while (this.shadowRoot.firstChild) {
      this.shadowRoot.removeChild(this.shadowRoot.firstChild);
    }
    
    // Append new template
    this.shadowRoot.appendChild(this.createTemplate());
    
    // Set up events
    this.setupEvents();
  }

  connectedCallback() {
    this.render();

    // Show immediately if show attribute is present
    if (this.hasAttribute('show')) {
      this.shouldBeVisible = true;
      this.createPortaledTooltip();
      this.portaledTooltip?.setAttribute('show', '');
      // Use setTimeout to ensure tooltip is rendered first
      setTimeout(() => this.updateTooltipPosition(), 0);
    }
  }

  disconnectedCallback() {
    // Clean up event listeners
    const wrapper = this.shadowRoot?.querySelector('.tooltip-wrapper');
    if (wrapper) {
      wrapper.removeEventListener('pointerenter', this.handleEvent);
      wrapper.removeEventListener('pointerleave', this.handleEvent);
      wrapper.removeEventListener('click', this.handleEvent);
    }

    // Clean up nested hover listener
    this.removeEventListener('as-tooltip-hover', this.handleNestedHover);

    // Clean up autoUpdate
    if (this.cleanup) {
      this.cleanup();
      this.cleanup = undefined;
    }

    // Clean up intersection observer
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = undefined;
    }

    // Clean up flash timeout
    if (this.flashTimeout) {
      clearTimeout(this.flashTimeout);
      this.flashTimeout = undefined;
    }

    document.removeEventListener('keydown', this._handlePinKeydown);
    document.removeEventListener('click', this._handlePinOutsideClick, true);
    this._pinned = false;
    // Remove portaled tooltip from body
    this.removePortaledTooltip();
  }
}

// Check if the element is already defined before registering it
if (!customElements.get('as-tooltip-wrapper')) {
  customElements.define('as-tooltip-wrapper', AsTooltipWrapper);
} else {
  console.warn("as-tooltip-wrapper already defined");
}
